import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';

import { ErrorState, LoadingState } from '../../components/ui/Feedback';
import { IconFileSpreadsheet, IconRefreshCw } from '../../components/layout/icons';
import { ApiError } from '../../lib/api-client';
import { useToast } from '../../lib/toast';
import { MrdEntryModal } from './MrdEntryModal';
import { mrdDepartmentConditionFor } from './mrd.mapping';
import { useGenerateMrdEntry, useMrdSummary } from './mrd.queries';
import type { MrdDepartmentRow, MrdDetailState } from './types';

interface RowEdit {
  opdAdditional: number;
  opdInput: number;
  ipdAdditional: number;
  ipdAdmit: number;
  emgAdditional: number;
  emgInput: number;
  otAdditional: number;
  otInput: number;
}

const emptyRowEdit: RowEdit = {
  opdAdditional: 0, opdInput: 0,
  ipdAdditional: 0, ipdAdmit: 0,
  emgAdditional: 0, emgInput: 0,
  otAdditional: 0, otInput: 0,
};

const rowEditFromRow = (row: MrdDepartmentRow): RowEdit => ({
  opdAdditional: row.opd.additional, opdInput: row.opd.input,
  ipdAdditional: row.ipd.additional, ipdAdmit: row.ipd.admit,
  emgAdditional: row.emg.additional, emgInput: row.emg.input,
  otAdditional: row.ot.additional, otInput: row.ot.input,
});

const todayInput = () => {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 10);
};

const rowInputTotal = (edit: RowEdit) => edit.opdInput + edit.ipdAdmit + edit.emgInput + edit.otInput;

const getRowEdit = (edits: Record<string, RowEdit>, row: MrdDepartmentRow) => edits[row.departmentId] ?? rowEditFromRow(row);

const toCount = (value: string): number => {
  const parsed = Math.floor(Number(value));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

export const MrdPage = () => {
  const [date, setDate] = useState(todayInput());
  const [edits, setEdits] = useState<Record<string, RowEdit>>({});
  const [pendingDepartmentId, setPendingDepartmentId] = useState<string | null>(null);
  const [activeDepartmentId, setActiveDepartmentId] = useState<string | null>(null);
  const seededDateRef = useRef<string | null>(null);
  const seededDepartmentsRef = useRef<Set<string>>(new Set());
  const { showToast } = useToast();

  const summaryQuery = useMrdSummary(date);
  const generateMutation = useGenerateMrdEntry();
  const summaryData = summaryQuery.data;
  const rows = useMemo(() => summaryData?.rows ?? [], [summaryData]);

  useEffect(() => {
    if (seededDateRef.current === date) return;
    seededDateRef.current = date;
    seededDepartmentsRef.current = new Set();
    setEdits({});
  }, [date]);

  useEffect(() => {
    if (!summaryData || summaryData.rows.length === 0) return;
    setEdits((current) => {
      let changed = false;
      const next = { ...current };
      for (const row of summaryData.rows) {
        if (seededDepartmentsRef.current.has(row.departmentId)) continue;
        seededDepartmentsRef.current.add(row.departmentId);
        next[row.departmentId] = rowEditFromRow(row);
        changed = true;
      }
      return changed ? next : current;
    });
  }, [summaryData]);

  const updateField = (departmentId: string, field: keyof RowEdit, value: string) => {
    const count = toCount(value);
    setEdits((current) => ({ ...current, [departmentId]: { ...(current[departmentId] ?? emptyRowEdit), [field]: count } }));
  };

  const activeRow = rows.find((row) => row.departmentId === activeDepartmentId) ?? null;
  const activeEdit = activeRow ? getRowEdit(edits, activeRow) : null;

  const closeModal = () => setActiveDepartmentId(null);

  const submitEntry = async (row: MrdDepartmentRow, detail: MrdDetailState) => {
    const edit = getRowEdit(edits, row);
    const patientRule = mrdDepartmentConditionFor(row);
    if (!patientRule) {
      showToast(`No patient generation mapping is configured for ${row.departmentName}.`, 'warning');
      return;
    }
    const emgIpdAdmissions = Math.round(edit.emgInput * 0.9);
    const requiredIpdBeds = edit.ipdAdmit + emgIpdAdmissions;
    if (requiredIpdBeds > row.availableBeds) {
      showToast(`Only ${row.availableBeds} IPD bed(s) are available within the 80% occupancy limit; ${requiredIpdBeds} are required including ${emgIpdAdmissions} from EMG.`, 'warning');
      return;
    }
    if (detail.avgOcc > row.maximumOccupiedBeds) {
      showToast(`Average occupancy cannot exceed ${row.maximumOccupiedBeds} beds (80% of ${row.bedCount}).`, 'warning');
      return;
    }
    const completeOtTotal = row.ot.actual + edit.otAdditional + edit.otInput;
    if (detail.otSectionEnabled && detail.otMajor + detail.otMinor !== completeOtTotal) {
      showToast(`Major (${detail.otMajor}) + Minor (${detail.otMinor}) must equal the complete OT total (${completeOtTotal}).`, 'warning');
      return;
    }
    setPendingDepartmentId(row.departmentId);
    try {
      const result = await generateMutation.mutateAsync({ date, departmentId: row.departmentId, ...edit, ...detail, patientRule });
      const generated = result.generation.generated;
      setEdits((current) => ({
        ...current,
        [row.departmentId]: {
          ...getRowEdit(current, row),
          opdAdditional: getRowEdit(current, row).opdAdditional + generated.opd,
          opdInput: 0,
          ipdAdditional: getRowEdit(current, row).ipdAdditional + generated.ipd,
          ipdAdmit: 0,
          emgAdditional: getRowEdit(current, row).emgAdditional + generated.emg,
          emgInput: 0,
          otAdditional: getRowEdit(current, row).otAdditional + generated.ot,
          otInput: 0,
        },
      }));
      const totalGenerated = generated.opd + generated.ipd + generated.emg + generated.ot;
      const investigationMessage = result.generation.investigationsGenerated > 0
        ? ` plus ${result.generation.investigationsGenerated} investigation(s)`
        : '';
      showToast(`${row.departmentName}: ${totalGenerated} patient record(s)${investigationMessage} generated across ${result.generation.doctorsUsed} doctor(s) (${result.generation.oldPatients} old, ${result.generation.newPatients} new).`, 'success');
      setActiveDepartmentId(null);
    } catch (reason) {
      const warning = reason instanceof ApiError && [
        'MRD_BED_CAPACITY_REACHED',
        'MRD_AVERAGE_OCCUPANCY_EXCEEDED',
        'MRD_OLD_PATIENTS_UNAVAILABLE',
        'MRD_OT_IPD_PATIENT_UNAVAILABLE',
        'MRD_OT_TYPE_TOTAL_MISMATCH',
        'MRD_OT_PROCEDURE_TYPE_UNAVAILABLE',
        'MRD_INVESTIGATION_TEST_UNAVAILABLE',
        'MRD_INVESTIGATION_PATIENT_UNAVAILABLE',
        'MRD_INVESTIGATION_TOTAL_BELOW_GENERATED',
      ].includes(reason.code);
      showToast(reason instanceof ApiError ? reason.message : 'The MRD entry could not be generated.', warning ? 'warning' : 'error');
    } finally {
      setPendingDepartmentId(null);
    }
  };

  const totals = useMemo(() => rows.reduce((sum, row) => {
    const edit = getRowEdit(edits, row);
    sum.opdActual += row.opd.actual; sum.opdAdditional += edit.opdAdditional; sum.opdInput += edit.opdInput;
    sum.ipdActual += row.ipd.actual; sum.ipdAdditional += edit.ipdAdditional; sum.ipdAdmit += edit.ipdAdmit;
    sum.emgActual += row.emg.actual; sum.emgAdditional += edit.emgAdditional; sum.emgInput += edit.emgInput;
    sum.otActual += row.ot.actual; sum.otAdditional += edit.otAdditional; sum.otInput += edit.otInput;
    return sum;
  }, {
    opdActual: 0, opdAdditional: 0, opdInput: 0,
    ipdActual: 0, ipdAdditional: 0, ipdAdmit: 0,
    emgActual: 0, emgAdditional: 0, emgInput: 0,
    otActual: 0, otAdditional: 0, otInput: 0,
  }), [rows, edits]);

  const errorMessage = summaryQuery.error instanceof ApiError ? summaryQuery.error.message : 'MRD summary could not be loaded.';

  return (
    <div className="page-stack mrd-page">
      <div className="mrd-titlebar">
        <span className="mrd-titlebar-icon" aria-hidden="true"><IconFileSpreadsheet /></span>
        <div>
          <h1>MRD — Medical Records Department</h1>
          <p>Department-wise Daily Patient Summary — OPD / IPD / EMG / OT</p>
        </div>
      </div>

      <section className="mrd-toolbar" aria-label="MRD controls">
        <div className="mrd-toolbar-left">
          <label className="mrd-date-field">
            <span>Date</span>
            <input type="date" value={date} max={todayInput()} onChange={(event) => setDate(event.target.value)} />
          </label>
          <button className="button button-secondary mrd-refresh-button" type="button" onClick={() => void summaryQuery.refetch()} disabled={summaryQuery.isFetching}>
            <IconRefreshCw />{summaryQuery.isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
          {summaryQuery.dataUpdatedAt ? <span className="mrd-last-updated">Last updated: {new Date(summaryQuery.dataUpdatedAt).toLocaleTimeString('en-IN', { hour12: false })}</span> : null}
        </div>
        <div className="mrd-toolbar-actions">
          <NavLink className="button button-secondary mrd-discharge-button" to="/mrd/mapping">MRD Logic</NavLink>
          {/* <NavLink className="button button-primary mrd-discharge-button" to="/mrd/discharge">Discharge</NavLink> */}
        </div>
      </section>

      <p className="mrd-hint">Enter OPD, IPD, EMG, and OT input before generate.</p>

      {summaryQuery.isLoading ? <LoadingState rows={10} columns={14} label="Loading MRD summary" /> : null}
      {summaryQuery.isError ? <ErrorState title="Unable to load MRD summary" message={errorMessage} action={{ label: 'Try again', onClick: () => void summaryQuery.refetch() }} /> : null}

      {summaryQuery.isSuccess ? (
        <div className={`mrd-table-wrap${summaryQuery.isFetching ? ' loading' : ''}`} aria-busy={summaryQuery.isFetching}>
          <table className="mrd-table">
            <thead>
              <tr className="mrd-group-row">
                <th rowSpan={2}>Department (Bed)</th>
                <th colSpan={3} className="mrd-group-opd">OPD</th>
                <th colSpan={3} className="mrd-group-ipd">IPD</th>
                <th colSpan={3} className="mrd-group-emg">EMG</th>
                <th colSpan={3} className="mrd-group-ot">OT</th>
                <th rowSpan={2}>Generate</th>
              </tr>
              <tr className="mrd-subhead-row">
                <th>Actual</th><th>Additional</th><th>Input</th>
                <th>Actual</th><th>Additional</th><th>Admit</th>
                <th>Actual</th><th>Additional</th><th>Input</th>
                <th>Actual</th><th>Additional</th><th>Input</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const edit = getRowEdit(edits, row);
                const dirtyTotal = rowInputTotal(edit);
                const pending = pendingDepartmentId === row.departmentId && generateMutation.isPending;
                return (
                  <tr key={row.departmentId} className={row.generated ? 'mrd-row-generated' : ''}>
                    <td className="mrd-dept-cell">
                      {row.departmentName} <span className="mrd-bed-count">({row.bedCount})</span>
                      <span className={`mrd-occupancy${row.availableBeds === 0 ? ' full' : ''}`}>
                        {row.occupiedBeds}/{row.maximumOccupiedBeds} occupied{row.availableBeds === 0 ? ' — 80% limit reached' : ''}
                      </span>
                    </td>

                    <td className="mrd-cell-actual">{row.opd.actual}</td>
                    <td className="mrd-cell-additional">{edit.opdAdditional}</td>
                    <td className="mrd-cell-input"><input type="number" min={0} inputMode="numeric" className="mrd-boxed-input" value={edit.opdInput} onChange={(event) => updateField(row.departmentId, 'opdInput', event.target.value)} aria-label={`${row.departmentName} OPD input`} /></td>

                    <td className="mrd-cell-actual">{row.ipd.actual}</td>
                    <td className="mrd-cell-additional">{edit.ipdAdditional}</td>
                    <td className="mrd-cell-input"><input type="number" min={0} max={row.availableBeds} inputMode="numeric" className="mrd-boxed-input" value={edit.ipdAdmit} onChange={(event) => updateField(row.departmentId, 'ipdAdmit', event.target.value)} aria-label={`${row.departmentName} IPD admit`} title={`${row.availableBeds} bed(s) available within the 80% limit`} /></td>

                    <td className="mrd-cell-actual">{row.emg.actual}</td>
                    <td className="mrd-cell-additional">{edit.emgAdditional}</td>
                    <td className="mrd-cell-input"><input type="number" min={0} inputMode="numeric" className="mrd-boxed-input" value={edit.emgInput} onChange={(event) => updateField(row.departmentId, 'emgInput', event.target.value)} aria-label={`${row.departmentName} EMG input`} /></td>

                    <td className="mrd-cell-actual">{row.ot.actual}</td>
                    <td className="mrd-cell-additional">{edit.otAdditional}</td>
                    <td className="mrd-cell-input"><input type="number" min={0} inputMode="numeric" className="mrd-boxed-input" value={edit.otInput} onChange={(event) => updateField(row.departmentId, 'otInput', event.target.value)} aria-label={`${row.departmentName} OT input`} /></td>

                    <td className="mrd-generate-cell">
                      <button
                        type="button"
                        className={`button mrd-generate-button${dirtyTotal > 0 ? ' active' : ''}`}
                        disabled={dirtyTotal === 0 || pending}
                        onClick={() => setActiveDepartmentId(row.departmentId)}
                        title={row.generated && row.generatedAt ? `Last generated ${new Date(row.generatedAt).toLocaleString('en-IN')}` : 'Generate this department’s daily entry'}
                      >
                        {pending ? 'Saving…' : row.generated ? 'Re-generate' : 'Generate'}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 ? <tr><td className="mrd-empty-cell" colSpan={14}>No active departments configured. Add departments in Settings to populate MRD.</td></tr> : null}
            </tbody>
            {rows.length > 0 ? (
              <tfoot>
                <tr className="mrd-total-row">
                  <td>Total</td>
                  <td>{totals.opdActual}</td><td>{totals.opdAdditional}</td><td>{totals.opdInput}</td>
                  <td>{totals.ipdActual}</td><td>{totals.ipdAdditional}</td><td>{totals.ipdAdmit}</td>
                  <td>{totals.emgActual}</td><td>{totals.emgAdditional}</td><td>{totals.emgInput}</td>
                  <td>{totals.otActual}</td><td>{totals.otAdditional}</td><td>{totals.otInput}</td>
                  <td>-</td>
                </tr>
                <tr className="mrd-actual-addl-row">
                  <td>Actual + Addl.</td>
                  <td colSpan={3}>{totals.opdActual + totals.opdAdditional}</td>
                  <td colSpan={3}>{totals.ipdActual + totals.ipdAdditional}</td>
                  <td colSpan={3}>{totals.emgActual + totals.emgAdditional}</td>
                  <td colSpan={3}>{totals.otActual + totals.otAdditional}</td>
                  <td>-</td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      ) : null}

      {activeRow && activeEdit ? (
        <MrdEntryModal
          row={activeRow}
          date={date}
          opdTotal={activeRow.opd.actual + activeEdit.opdAdditional + activeEdit.opdInput}
          ipdTotal={activeRow.ipd.actual + activeEdit.ipdAdditional + activeEdit.ipdAdmit}
          emgTotal={activeRow.emg.actual + activeEdit.emgAdditional + activeEdit.emgInput}
          otTotal={activeRow.ot.actual + activeEdit.otAdditional + activeEdit.otInput}
          submitting={pendingDepartmentId === activeRow.departmentId && generateMutation.isPending}
          onClose={closeModal}
          onSubmit={(detail) => void submitEntry(activeRow, detail)}
        />
      ) : null}
    </div>
  );
};
